import { useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { productsData } from '../../mocks/products';
import { SEO } from '../../utils/seo';

export default function ProductsPage() {
  const [activeCategory, setActiveCategory] = useState('すべて');
  const [expandedProductIds, setExpandedProductIds] = useState<number[]>([]);

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  const productSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: '商品一覧 | おかやま健康製薬株式会社',
    description: '岡山のおかやま健康製薬が提供する全世代対応の高品質サプリメント商品一覧。若年層向け、働き盛り世代向け、シニア世代向けなど、目的別に選べる豊富なラインナップ。',
    url: `${siteUrl}/products`,
    inLanguage: 'ja-JP',
    isPartOf: {
      '@type': 'WebSite',
      name: 'おかやま健康製薬株式会社',
      url: siteUrl,
    },
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'ホーム',
          item: siteUrl,
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: '商品一覧',
          item: `${siteUrl}/products`,
        },
      ],
    },
  };

  const categories = [
    'すべて',
    'ユース世代向け',
    'ミドル世代向け',
    'シニア世代向け',
    '妊婦向け',
    '子供向け',
    '栄養補助',
  ];

  const filteredProducts =
    activeCategory === 'すべて'
      ? productsData
      : productsData.filter((product) => product.category === activeCategory);

  const toggleProductExpansion = (productId: number) => {
    setExpandedProductIds((prev) =>
      prev.includes(productId)
        ? prev.filter((id) => id !== productId)
        : [...prev, productId]
    );
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="商品一覧 | 岡山の健康サプリメント | おかやま健康製薬株式会社"
        description="岡山のおかやま健康製薬が提供する全世代対応の高品質サプリメント商品一覧。若年層向け、働き盛り世代向け、シニア世代向けなど、目的別に選べる豊富なラインナップ。科学的根拠に基づいた安心・安全な製品で家族の健康をサポートします。"
        keywords="岡山,サプリメント商品,健康食品一覧,若年層向け,働き盛り世代,シニア向け,おかやま健康製薬"
        canonical={`${siteUrl}/products`}
        schema={productSchema}
      />
      <Header />

      {/* Hero Section */}
      <section className="relative h-[40vh] mt-16 lg:mt-20 bg-gradient-to-br from-[#4A90E2] to-[#2A6D8D] flex items-center justify-center">
        <div className="absolute inset-0 bg-[url('https://public.readdy.ai/ai/img_res/73d6301c9a30ff2c2f42fda8be3d50e6.jpg')] bg-cover bg-center opacity-10"></div>
        <div className="relative text-center text-white px-4">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4">商品一覧</h1>
          <p className="text-base sm:text-lg lg:text-xl">
            あなたの健康目標に合わせた最適なサプリメントを
          </p>
        </div>
      </section>

      {/* Filter Section */}
      <section className="sticky top-16 lg:top-20 z-40 bg-white shadow-md">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-hide">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setActiveCategory(category)}
                className={`px-4 lg:px-6 py-2 rounded-full font-medium text-sm whitespace-nowrap transition-all cursor-pointer ${
                  activeCategory === category
                    ? 'bg-[#4A90E2] text-white shadow-lg'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Products Grid */}
      <section className="py-12 lg:py-16">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8" data-product-shop>
            {filteredProducts.map((product) => (
              <article
                key={product.id}
                className="bg-white rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2"
              >
                <div className="aspect-square w-full h-64 sm:h-72 lg:h-80 bg-white p-6">
                  <img
                    src={product.image}
                    alt={product.name}
                    className="w-full h-full object-contain"
                  />
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="px-3 py-1 bg-[#E8F4F8] text-[#4A90E2] text-xs font-medium rounded-lg">
                      {product.category}
                    </span>
                    <span className="text-xs text-gray-500">{product.targetAge}</span>
                  </div>
                  <h3 className="text-xl font-bold text-[#1A4D6D] mb-3">{product.name}</h3>
                  <p className="text-sm text-gray-600 mb-4 leading-relaxed line-clamp-2">
                    {product.description}
                  </p>
                  <div className="flex flex-wrap gap-2 mb-6">
                    {product.features.map((feature, index) => (
                      <span
                        key={index}
                        className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded"
                      >
                        {feature}
                      </span>
                    ))}
                  </div>
                  <button
                    onClick={() => toggleProductExpansion(product.id)}
                    className="w-full px-6 py-3 border-2 border-[#4A90E2] text-[#4A90E2] rounded-lg font-medium hover:bg-[#4A90E2] hover:text-white transition-all whitespace-nowrap cursor-pointer flex items-center justify-center gap-2"
                  >
                    {expandedProductIds.includes(product.id) ? '閉じる' : '詳細を見る'}
                    <i className={`ri-arrow-${expandedProductIds.includes(product.id) ? 'up' : 'down'}-s-line text-lg w-5 h-5 flex items-center justify-center`}></i>
                  </button>

                  {expandedProductIds.includes(product.id) && product.details && (
                    <div className="mt-5 pt-5 border-t border-gray-200 space-y-4 animate-fadeIn">
                      <div>
                        <h4 className="text-xs font-bold text-[#1A4D6D] mb-1 flex items-center gap-1.5">
                          <i className="ri-file-list-3-line text-sm w-4 h-4 flex items-center justify-center text-[#4A90E2]"></i>
                          原材料名
                        </h4>
                        <p className="text-xs text-gray-600 leading-relaxed pl-5.5">{product.details.ingredients}</p>
                      </div>
                      <div>
                        <h4 className="text-xs font-bold text-[#1A4D6D] mb-1 flex items-center gap-1.5">
                          <i className="ri-alert-line text-sm w-4 h-4 flex items-center justify-center text-[#E2784A]"></i>
                          アレルギー表記
                        </h4>
                        <p className={`text-xs leading-relaxed pl-5.5 ${product.details.allergens === '該当なし' ? 'text-gray-500' : 'text-[#E2784A] font-medium'}`}>{product.details.allergens}</p>
                      </div>
                      <div>
                        <h4 className="text-xs font-bold text-[#1A4D6D] mb-1 flex items-center gap-1.5">
                          <i className="ri-box-3-line text-sm w-4 h-4 flex items-center justify-center text-[#4A90E2]"></i>
                          内容量
                        </h4>
                        <p className="text-xs text-gray-600 leading-relaxed pl-5.5">{product.details.content}</p>
                      </div>
                      <div>
                        <h4 className="text-xs font-bold text-[#1A4D6D] mb-1 flex items-center gap-1.5">
                          <i className="ri-test-tube-line text-sm w-4 h-4 flex items-center justify-center text-[#4A90E2]"></i>
                          成分表
                        </h4>
                        <p className="text-xs text-gray-600 leading-relaxed pl-5.5">{product.details.nutrition}</p>
                      </div>
                      <div>
                        <h4 className="text-xs font-bold text-[#1A4D6D] mb-1 flex items-center gap-1.5">
                          <i className="ri-capsule-line text-sm w-4 h-4 flex items-center justify-center text-[#4A90E2]"></i>
                          摂取方法
                        </h4>
                        <p className="text-xs text-gray-600 leading-relaxed pl-5.5">{product.details.usage}</p>
                      </div>
                      <div className="bg-[#FFF8F0] rounded-lg p-3">
                        <h4 className="text-xs font-bold text-[#E2784A] mb-1 flex items-center gap-1.5">
                          <i className="ri-error-warning-line text-sm w-4 h-4 flex items-center justify-center"></i>
                          ご注意
                        </h4>
                        <p className="text-xs text-gray-600 leading-relaxed pl-5.5">{product.details.caution}</p>
                      </div>
                    </div>
                  )}
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 lg:py-16 bg-[#F5FAFE]">
        <div className="max-w-[900px] mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#1A4D6D] mb-6">
            お気軽にご相談ください
          </h2>
          <p className="text-sm lg:text-base text-gray-600 mb-8 leading-relaxed">
            どの商品があなたに最適か迷われている方は、お電話またはお問い合わせフォームからお気軽にご相談ください。
            <br className="hidden sm:block" />
            専門スタッフが丁寧にご案内いたします。
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="tel:0860123456"
              className="inline-flex items-center justify-center px-6 lg:px-8 py-3 lg:py-4 bg-[#1A4D6D] text-white rounded-lg font-medium hover:bg-[#0F3A52] transition-all shadow-lg whitespace-nowrap cursor-pointer"
            >
              <i className="ri-phone-line mr-2 text-lg"></i>
              086-012-3456
            </a>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center px-6 lg:px-8 py-3 lg:py-4 border-2 border-[#1A4D6D] text-[#1A4D6D] rounded-lg font-medium hover:bg-[#1A4D6D] hover:text-white transition-all whitespace-nowrap"
            >
              お問い合わせフォーム
              <i className="ri-arrow-right-line ml-2"></i>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}